import React from 'react';
import PropTypes from 'prop-types';

import InlineSvg from './inline_svg';
import ExternalSvg from './external_svg';

/**
 * Renders an icon. Supports:
 *
 * * all image formats
 * * SVG passed as text
 * * React components
 * * React elements
 *
 * SVG icons can be inlined, which comes in handy when CSS styles must be applied or inherited in
 * them.
 */
export default function Icon({ alt, height, inline = false, src, width }) {
  if (!src) {
    return null;
  }

  if (typeof src !== 'string') {
    if (typeof src === 'object') {
      if (React.isValidElement(src)) {
        return React.cloneElement(src, { alt, height, width });
      }

      return React.createElement(src, { alt, height, width });
    }

    return src;
  }

  const props = {
    width,
    height,
  };

  if (inline) {
    if (src.endsWith('svg')) {
      return <ExternalSvg {...props} src={src} />;
    }

    if (src.includes('<svg')) {
      return <InlineSvg {...props}>{src}</InlineSvg>;
    }
  }

  return <img {...props} alt={alt} src={src} />;
}

Icon.propTypes = {
  /**
   * Alternative text. Pass `""` for decorative elements.
   */
  alt: PropTypes.string.isRequired,
  /**
   * Height in pixels (recommended if icon URL is used).
   */
  height: PropTypes.number,
  /**
   * Whether to inline the icon. Only applicable to SVG icons. This comes in handy when CSS styles
   * must be applied or inherited in SVG icon.
   */
  inline: PropTypes.bool,
  /**
   * Icon, icon source or icon URL.
   */
  src: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.object, PropTypes.string])
    .isRequired,
  /**
   * Width in pixels (recommended if icon URL is used).
   */
  width: PropTypes.number,
};
